<script>
    import snapshotService from "./service.js";
    import SnapshotReportRow from "../../components/powerTable/snapshot-report-row.svelte";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import { PowerTable } from "@muonw/powertable";
    import { format, locale } from "svelte-i18n";
    import { onMount, tick, onDestroy } from "svelte";
    import * as bootstrap from "bootstrap";
    import powerTableService from "../../services/powerTable.js";
    import ReportModal from "./_components/report-modal.svelte";
    import ComparisonModal from "./_components/comparison-modal.svelte";
    import dayjs from "dayjs";
    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import { user } from "../user/user.state.svelte.js";
    import { notificate } from "../../services/notification.js";
    import "dayjs/locale/es";
    import "dayjs/locale/en";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    let snapshots = $state({ data: [] });
    let tableElement = $state();
    let is_requesting = $state(false);
    let selected_rows = $state({ data: [] });
    let snapshot = $state({ data: {} });
    let snapshotsToCompare = $state({ snapshot_1: {}, snapshot_2: {} });
    let snapshotsComparison = $state({ data: [] });
    let reportModal;
    let comparisonModal;
    let filters = $state({
        asset_tag: "",
        finding_tag: "",
        vulnerability_tag: "",
        asset_kind__in: "",
    });
    let error = $state();
    let loadingHTML = $state(false);
    let loadingCustom = $state(false);
    let select2Element;
    let checkbox;

    // NUCLEAR OPTION: Intercepts all close triggers natively
    const handleModalDismiss = (e, modalInstance) => {
        if (e.target.closest('[data-bs-dismiss="modal"]')) {
            e.preventDefault();
            if (modalInstance) modalInstance.hide();
        }
    };

    /* UTILS */
    //@TODO: Move to utils

    /**
     * Returns string with formatted date used in report download fileName.
     *
     */
    const getFormattedDate = (dateString) => {
        return dayjs(dateString).format("YYYYMMDD");
    };

    /* FETCH, DOWNLOAD AND DATA MANIPULATION FUNCTIONS */

    const saveNewSnapshot = async () => {
        let result = await notificate("confirm_new_snapshot", "modal", {});
        if (!result.isConfirmed) {
            return false;
        } else {
            let response = await snapshotService.save(false);
            await getData();
        }
        return true;
    };

    const getSnapshotDetail = async (id, config = {}) => {
        if (config.resetFilters) resetFilters(filters, config.resetFilters);
        let response = await snapshotService.find(id, config);
        return (snapshot.data = response);
    };

    const updateFilterValue = (filtersData, key, value) => {
        filtersData[key] = value;
    };

    const resetFilters = (filtersData, keys = []) => {
        for (let key of keys) {
            filtersData[key] = "";
        }
    };

    const handleReportFilters = async (id, filter) => {
        let config = { filter: {} };
        let key = Object.keys(filter)[0];
        filters[key] = filter[key];
        let filtersCopy = JSON.parse(JSON.stringify($state.snapshot(filters)));
        for (let [k, v] of Object.entries(filtersCopy)) {
            if (v.length && k != "format" && k != "language")
                config.filter[k] = v;
        }
        if (id) await getSnapshotDetail(id, config);
    };

    const updateReqState = (item) => {
        return (is_requesting = !item);
    };

    const getData = async () => {
        updateReqState(is_requesting);

        let snapshotsPromise = snapshotService.search().then((pSnapshots) => {
            pSnapshots = pSnapshots.filter((sshot) => !sshot.ephemeral);
            pSnapshots = pSnapshots.sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
            return (snapshots.data = pSnapshots);
        });
        await snapshotsPromise;
        await tick();
        updateReqState(is_requesting);
        return true;
    };

    const handleReportDownload = async (
        config = { filter: { language: $locale } },
        summary_id,
        should_save = false,
    ) => {
        updateReqState(is_requesting);
        if (error) error = null;

        let file_name;
        let cr_at = getFormattedDate(snapshot.data.created_at);
        if (config.filter.language.includes("es")) {
            if (config.filter.format.includes("html")) {
                file_name = `${cr_at}.vulseek.reporte.html`;
            } else if (config.filter.format.includes("markdown")) {
                file_name = `${cr_at}.vulseek.reporte.md`;
            } else {
                file_name = `${cr_at}.vulseek.reporte.pdf`;
            }
        }
        if (
            !config ||
            !config.filter.language ||
            config.filter.language.includes("en")
        ) {
            if (config.filter.format && config.filter.format.includes("html")) {
                file_name = `${cr_at}.vulseek.report.html`;
            } else if (config.filter.format.includes("markdown")) {
                file_name = `${cr_at}.vulseek.report.md`;
            } else {
                file_name = `${cr_at}.vulseek.report.pdf`;
            }
        }

        loadingHTML = true;

        let file = await snapshotService
            .getReport(config, summary_id, snapshot.data.id)
            .catch((e) => {
                loadingHTML = false;
                loadingCustom = false;
                error = e;
            });

        if (should_save) {
            await saveReport(config, summary_id, snapshot.data.id).catch(
                (e) => {
                    loadingHTML = false;
                    loadingCustom = false;
                    error = e;
                },
            );
        }

        if (file) {
            const fileBlob = await file.blob();
            const fileUrl = URL.createObjectURL(fileBlob);
            const anchor = document.createElement("a");
            anchor.href = fileUrl;
            anchor.download = file_name;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            URL.revokeObjectURL(fileUrl);
        }
        loadingHTML = false;
        updateReqState(is_requesting);
        getData();
    };

    const saveReport = async (config, summary_id, snapshot_id) => {
        await snapshotService.saveReport(config, summary_id, snapshot_id);
    };

    /**
     * Returns diff of deep objects snapshots count
     *@param count_1 object with counts by kind or level
     *@param count_2 object with counts by kind or level
     */

    const compareSnapshotsCountByElementKind = (count_1 = {}, count_2 = {}) => {
        let diffByKind = {};
        for (let key_1 of Object.keys(count_1)) {
            for (let key_2 of Object.keys(count_2)) {
                //if there are 0 elements key/value pair is not defined, set 0 to avoid undefined/NaN results.
                if (!count_1[key_1]) count_1[key_1] = 0;
                if (!count_2[key_1]) count_2[key_1] = 0;
                diffByKind[key_1] = {
                    oldSnapshotValue: count_1[key_1],
                    newSnapshotValue: count_2[key_1],
                    diff: count_2[key_1] - count_1[key_1],
                };
                if (!Object.keys(count_1).includes(key_2)) {
                    if (!count_2[key_2]) count_2[key_2] = 0;
                    if (!count_1[key_2]) count_1[key_2] = 0;
                    diffByKind[key_2] = {
                        oldSnapshotValue: 0,
                        newSnapshotValue: count_2[key_2],
                        diff: count_2[key_2],
                    };
                }
            }
        }
        return diffByKind;
    };

    const compareSnapshotsCountByElementLevel = (
        count_1 = {},
        count_2 = {},
    ) => {
        let diffByLevel = {
            critical: {
                oldSnapshotValue: 0,
                newSnapshotValue: 0,
                diff: 0,
            },
            high: {
                oldSnapshotValue: 0,
                newSnapshotValue: 0,
                diff: 0,
            },
            medium: {
                oldSnapshotValue: 0,
                newSnapshotValue: 0,
                dff: 0,
            },
            low: {
                oldSnapshotValue: 0,
                newSnapshotValue: 0,
                diff: 0,
            },
        };
        for (let key_1 of Object.keys(count_1)) {
            for (let key_2 of Object.keys(count_2)) {
                diffByLevel[key_1] = {
                    oldSnapshotValue: count_1[key_1],
                    newSnapshotValue: count_2[key_1] || 0,
                    diff: (count_2[key_1] || 0) - (count_1[key_1] || 0),
                };
                if (!Object.keys(count_1).includes(key_2)) {
                    diffByLevel[key_2] = {
                        oldSnapshotValue: 0,
                        newSnapshotValue: count_2[key_2],
                        diff: count_2[key_2],
                    };
                }
            }
        }
        return diffByLevel;
    };

    /**
     * Returns string with formatted date used in report download fileName.
     *@param selected Snapshots data to get IDs. Array with length = 2 (with more or less elements selected button is disabled)
     */

    const compareSnapshots = async (selected = [{ id: "" }, { id: "" }]) => {
        snapshotsComparison.data = [];
        // Put older snapshot always at pos 0. Positive result means count was lower for the older snapshot, negative means it was higher
        selected = selected.sort((a, b) => {
            return new Date(a.created_at) - new Date(b.created_at);
        });
        snapshotsToCompare.snapshot_1 = await getSnapshotDetail(selected[0].id);
        snapshotsToCompare.snapshot_2 = await getSnapshotDetail(selected[1].id);
        let snapshotsCompared = {
            snapshot_1: $state.snapshot(snapshotsToCompare.snapshot_1),
            snapshot_2: $state.snapshot(snapshotsToCompare.snapshot_2),
            assets_count: {
                oldSnapshotValue: snapshotsToCompare.snapshot_1.assets_count,
                newSnapshotValue: snapshotsToCompare.snapshot_2.assets_count,
                diff:
                    snapshotsToCompare.snapshot_2.assets_count -
                    snapshotsToCompare.snapshot_1.assets_count,
            },
            findings_count: {
                oldSnapshotValue: snapshotsToCompare.snapshot_1.findings_count,
                newSnapshotValue: snapshotsToCompare.snapshot_2.findings_count,
                diff:
                    snapshotsToCompare.snapshot_2.findings_count -
                    snapshotsToCompare.snapshot_1.findings_count,
            },
            vulnerabilities_count: {
                oldSnapshotValue:
                    snapshotsToCompare.snapshot_1.vulnerabilities_count,
                newSnapshotValue:
                    snapshotsToCompare.snapshot_2.vulnerabilities_count,
                diff:
                    snapshotsToCompare.snapshot_2.vulnerabilities_count -
                    snapshotsToCompare.snapshot_1.vulnerabilities_count,
            },
            assets_count_by_kind: compareSnapshotsCountByElementKind(
                snapshotsToCompare.snapshot_1.assets_count_by_kind,
                snapshotsToCompare.snapshot_2.assets_count_by_kind,
            ),
            findings_count_by_level: compareSnapshotsCountByElementLevel(
                snapshotsToCompare.snapshot_1.findings_count_by_level,
                snapshotsToCompare.snapshot_2.findings_count_by_level,
            ),
            vulnerabilities_count_by_level: compareSnapshotsCountByElementLevel(
                snapshotsToCompare.snapshot_1.vulnerabilities_count_by_level,
                snapshotsToCompare.snapshot_2.vulnerabilities_count_by_level,
            ),
        };
        snapshotsComparison.data = snapshotsCompared;
        if (comparisonModal) comparisonModal.show();
        return snapshotsCompared;
    };

    /* FORM ELEMENTS FUNCTIONS */

    const resetSelect2 = () => {
        select2Element.val([]);
        select2Element.trigger("change");
    };

    const setUpSelect2 = () => {
        select2Element = jQuery(".s2").select2({
            placeholder: $format("page.snapshot.select"),
        });
        select2Element.on("select2:select", function (e) {
            // Action when an option is selected
            let data = e.params.data;
            // Call your custom function here
            filters.asset_kind__in =
                filters.asset_kind__in.length > 0
                    ? filters.asset_kind__in + "," + data.id
                    : data.id;
            handleReportFilters($state.snapshot(snapshot.data.id), filters);
        });
        select2Element.on("select2:unselect", function (e) {
            // Action when an option is selected
            let data = e.params.data;
            // Call your custom function here
            let newValue = filters.asset_kind__in.includes(",")
                ? filters.asset_kind__in.replace(`${data.id},`, "")
                : filters.asset_kind__in.replace(`${data.id}`, "");
            filters.asset_kind__in = newValue;
            handleReportFilters($state.snapshot(snapshot.data.id), filters);
        });
        resetSelect2();
    };

    /* LIST FUNCTIONS */
    const parseRows = (pageContent) => {
        pageContent.forEach((row, i) => {
            if (row["created_at"]) {
                row["created_at"] = dayjs(row["created_at"]).format(
                    "YYYY/MM/DD, hh:mm:ss",
                );
            }
            row.download_report = {
                id: row.id,
                created_at: row.created_at,
                getSnapshotDetail: getSnapshotDetail,
            };
        });
        deselectAll();
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "snapshotsTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            /*'topBar': ['search'],*/
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let instructs = [
        {
            key: "select",
            title: "",
            filterable: false,
            sortable: false,
        },
        {
            key: "created_at",
            title: $format("list.columns.created"),
            filterable: false,
        },
        {
            key: "download_report",
            title: "",
            filterable: false,
            sortable: false,
            parseAs: "component",
            dataComponent: SnapshotReportRow,
        },
    ];

    const selectAll = async (e) => {
        let tData = tableElement.getData(false, ["pageData", "matchedData"]);
        let pageData = tData.pageData;
        if (!pageData || pageData.length === 0) return selected_rows;
        // 1. Check if the page is fully selected to determine select vs deselect mode
        const selectedIds = new Set(selected_rows.data.map((r) => r.id));
        const allPageSelected = pageData.every((el) => selectedIds.has(el.id));

        let action = allPageSelected ? "deselect" : "select";

        // 2. Handle selection logic
        if (action === "select") {
            let result;
            if (tData.matchedData.length > 10) {
                result = await notificate("select_all", "modal", {});

                if (!result.isConfirmed && result.dismiss !== "cancel") {
                    return selected_rows;
                }
            }

            let target_elements =
                result && result.isConfirmed ? tData.matchedData : pageData;
            const currentIds = new Set(selected_rows.map((x) => x.id));
            let toAdd = target_elements.filter((x) => !currentIds.has(x.id));

            selected_rows = [...selected_rows, ...toAdd];
        } else {
            // Deselect mode: clear everything
            selected_rows = [];
        }

        // 3. Update DOM and action buttons
        powerTableService.toggleActionButtons(selected_rows.data);

        const domRows = document.querySelectorAll(
            `div[data-prefix='snapshotsTable'] table tbody tr`,
        );

        // Map the PT_ID to a fast lookup set of strict strings
        const selectedPtIds = new Set(
            selected_rows.data.map((r) => String(r.__PT_ID__)),
        );

        domRows.forEach((row) => {
            let rowId = row.getAttribute("data-id");
            if (selectedPtIds.has(rowId)) {
                row.classList.add("selected");
            } else {
                row.classList.remove("selected");
            }
        });

        return selected_rows;
    };

    const deselectAll = () => {
        selected_rows.data = powerTableService.deselectAll();
        powerTableService.toggleActionButtons(selected_rows.data);
        return selected_rows.data;
    };

    const selectAllEventListener = () => {
        checkbox = document.querySelector(
            "th[data-key='select'][data-sortable='false']",
        );
        if (checkbox) {
            checkbox.addEventListener("click", selectAll);
        }
    };

    const selectRow = (event) => {
        let tData = tableElement.getData(false, [
            "options",
            "instructs",
            "data",
            "search",
            "filters",
            "pageData",
            "matchedData",
        ]);
        let current = event.detail.data;
        let idx = selected_rows.data.findIndex((r) => r.id == current.id);
        selected_rows.data = powerTableService.selectRows(
            selected_rows.data,
            current,
            idx,
            "snapshotsTable",
            tData.pageData,
        );
        let shouldDisableBtns = false;
        if (shouldDisableBtns) {
            let btns = document.getElementsByClassName("btn-disabled");
            for (let btn of btns) {
                btn.setAttribute("disabled", true);
            }
        } else {
            powerTableService.toggleActionButtons(
                $state.snapshot(selected_rows.data),
            );
        }
        return $state.snapshot(selected_rows.data);
    };

    onMount(async () => {
        await tick();
        const bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
        if (bs) {
            const rModalEl = document.getElementById("snapshot-report-modal");
            if (rModalEl) reportModal = new bs.Modal(rModalEl);

            const cModalEl = document.getElementById("snapshot-comparison-modal");
            if (cModalEl) comparisonModal = new bs.Modal(cModalEl);
        }
        
        await getData();
        selectAllEventListener();
    });
    onDestroy(() => {
        if (checkbox) checkbox.removeEventListener("click", selectAll);
    });
</script>

<svelte:window onclick={(e) => {
    if (e.target.closest('[data-bs-target="#snapshot-report-modal"]')) {
        e.preventDefault();
        if (reportModal) reportModal.show();
    }
}} />

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i class="fas fa-camera fa-xs"></i>
                    {$format("page.snapshot.title")}
                </h1>
            </div>
            <div class="col-6">
                <BreadCrumNav
                    routes={[{ label: $format("page.snapshot.title") }]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="card">
            <div class="card-header">
                <div class="card-tools float-end">
                    {#if user.data.is_internal}
                        <button
                            class="btn btn-secondary table-button btn-add btn-xs t-compare-snapshot-btn"
                            tabindex="0"
                            aria-controls="snapshotTable"
                            type="button"
                            title={$format("buttons.create_snapshot")}
                            id="snapshotsCreateBtn"
                            aria-label="create"
                            style="max-width: fit-content !important; width: fit-content !important; padding-left: 5px; padding-right: 5px;"
                            onclick={async () => await saveNewSnapshot()}
                        >
                            <div class="d-flex align-items-center">
                                <span
                                    >{$format(
                                        "page.snapshot.create_snapshot",
                                    )}</span
                                >
                            </div>
                        </button>
                    {/if}
                    <button
                        class="btn btn-secondary table-button btn-disabled btn-compare btn-xs t-compare-snapshot-btn"
                        tabindex="0"
                        aria-controls="snapshotTable"
                        disabled
                        type="button"
                        title={$format("buttons.compare")}
                        id="snapshotsCompareBtn"
                        aria-label="compare"
                        style="max-width: fit-content !important; width: fit-content !important; padding-left: 5px; padding-right: 5px;"
                        onclick={async () =>
                            await compareSnapshots(
                                $state.snapshot(selected_rows.data),
                            )}
                    >
                        <div class="d-flex align-items-center">
                            <span style="font-size: 16px; width: fit-content;"
                                >{$format("page.snapshot.compare")}</span
                            >
                            &nbsp;<i
                                style="font-size: 14px;"
                                class="fas fa-balance-scale-left"
                            ></i>
                        </div>
                    </button>
                    <button
                        class="btn btn-secondary table-button btn-refresh btn-xs t-refresh-snapshot-btn"
                        tabindex="0"
                        aria-controls="snapshotTable"
                        type="button"
                        title={$format("buttons.refresh")}
                        id="snapshotRefreshBtn"
                        aria-label="refresh"
                        onclick={getData}
                    >
                        <i class="fa fa-sync-alt"></i>
                    </button>
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <div class="overlay-wrapper">
                        <div class="text-center" class:overlay={is_requesting}>
                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                        </div>
                    </div>
                    <PowerTable
                        ptData={snapshots.data}
                        ptInstructs={instructs}
                        {ptOptions}
                        bind:this={tableElement}
                        on:rowClicked={(e) => selectRow(e)}
                    >
                        <div slot="noResults">
                            {$format("results_not_found")}
                        </div>
                    </PowerTable>
                </div>
            </div>
        </div>
    </div>
</section>

<!-- Wrap modals in the native Svelte dismiss handler -->
<div onclickcapture={(e) => handleModalDismiss(e, reportModal)}>
    <ReportModal
        snapshot={snapshot.data}
        {filters}
        showBtn={false}
        {handleReportFilters}
        {handleReportDownload}
        {is_requesting}
        {setUpSelect2}
        {resetSelect2}
    />
</div>

<div onclickcapture={(e) => handleModalDismiss(e, comparisonModal)}>
    <ComparisonModal {snapshotsComparison} />
</div>

<style>
    .overlay-wrapper div {
        display: none;
    }
    .overlay-wrapper div.overlay {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 20px;
    }
    .table-button.btn.btn-secondary.btn-add span{
        padding-top: 0 !important;
    }
</style>